/**
 * One error type for every failed call. The platform API always answers errors with
 * `{"error": {"code", "message"}}`; transport failures and unexpected bodies are mapped to the
 * same shape so the apps only ever check `status` and `code`.
 */
import type { ConfirmationRequiredBody, ErrorBody } from './types';

/** Codes produced by the client itself (the server's codes are passed through unchanged). */
export const CLIENT_ERROR_CODES = {
  network: 'network_error',
  invalidResponse: 'invalid_response',
} as const;

export class ApiError extends Error {
  constructor(
    /** HTTP status, 0 when the server could not be reached. */
    readonly status: number,
    /** Stable machine-readable code, e.g. `invalid_credentials`, `not_admin`, `csrf_failed`. */
    readonly code: string,
    message: string,
    options?: { cause?: unknown; body?: unknown },
  ) {
    super(message, options);
    this.body = options?.body;
    this.name = 'ApiError';
  }

  /** The full error body (e.g. `issues` of a rejected language pack). */
  readonly body: unknown;

  /** True for a missing/expired session (the app sends the user back to the login). */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export function isApiError(error: unknown, code?: string): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

/**
 * The text a 428 `confirmation_required` asks the user to type (server-side `error.details.expected`, e.g. the host
 * name of a certificate slot); undefined for any other error or when the server sent none. Reads `code` and `body`
 * of any error object (ApiError, or the shape the apps' mock APIs throw).
 */
export function confirmationExpected(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const { code, body } = error as { code?: unknown; body?: unknown };
  if (code !== 'confirmation_required') return undefined;
  const expected = (body as Partial<ConfirmationRequiredBody> | undefined)?.error?.details?.expected;
  return typeof expected === 'string' && expected !== '' ? expected : undefined;
}

function isErrorBody(value: unknown): value is ErrorBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const { error } = value;
  return typeof error === 'object' && error !== null && typeof (error as { code?: unknown }).code === 'string';
}

/** Builds the ApiError for a non-2xx response. */
export async function errorFromResponse(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }
  if (isErrorBody(body)) return new ApiError(response.status, body.error.code, body.error.message, { body });
  return new ApiError(response.status, `http_${response.status}`, response.statusText || `HTTP ${response.status}`);
}
